import type { NextPage } from 'next'

import {
    activityStreamBigImage,
    fieldGroupsEditingImage,
    pageHomeImage,
    pagePersonDetailsImage,
    pageTableImage,
    stackCardsImage,
    tableBulkEditingImage,
    tableLeadsSearchImage,
    tablePipelineImage,
} from 'src/libs/assets'
import { PageLayout } from 'src/libs/layout'
import { FeatureGroup } from 'src/libs/page-features'
import {
    BannerWithCtaSection,
    ClosingSection,
    FaqSection,
    IntroSectionSimple,
    RequestDemoButton,
    TeaserCardSliderSection,
} from 'src/libs/ui-shared'

const FeaturesPage: NextPage = () => {
    return (
        <PageLayout>
            <div className="grid gap-40">
                <IntroSectionSimple
                    title="Flexible, intuitive features for any sales pipeline"
                    description="Your company chose Salesforce as the system of record. But that doesn’t mean that you have to forgo modern features. Improve all your Salesforce workflows with Laserfocus."
                >
                    <RequestDemoButton />
                </IntroSectionSimple>

                <FeatureGroup
                    title="The right task at the right time"
                    image={pageHomeImage}
                    imageAlt="Home page with tasks overview"
                    imageBgColor="yellow"
                    sections={[
                        {
                            title: 'Your processes made simple with stacks',
                            description:
                                'Don’t get overloaded with too much information. Laserfocus creates clear-to-follow workflows, so your team stays focused on what’s important now. We call them stacks.',
                            benefits: [
                                'Create and share ad-hoc stacks within your team to work on',
                                'Set automated stacks for recurring events',
                                'Get everyone up to speed without micro managing',
                            ],
                            benefitsColor: 'blue',
                            image: stackCardsImage,
                            imageAlt: 'Stack cards',
                        },
                        {
                            title: 'Make your process intuitive',
                            description:
                                'No need for long training and longer ramp-up time in Salesforce. Laserfocus ensures everyone knows to work on with tasks and custom stacks.',
                            benefits: [
                                'Create actionable tasks in second for every deal',
                                'Create stacks according to different roles and processes in your team',
                                'See live progress on everything the team is working on',
                            ],
                            benefitsColor: 'purple',
                            image: tablePipelineImage,
                            imageAlt: 'Pipeline table',
                        },
                    ]}
                />

                <FeatureGroup
                    title="Powerful like reports, simple as spreadsheets"
                    image={pageTableImage}
                    imageAlt="Table page with all my leads"
                    imageBgColor="blue"
                    sections={[
                        {
                            title: 'See fields you need and update them in one go',
                            description:
                                'Creating a view in Laserfocus doesn’t require the help of admins or complex reports. Just add any field from any object and edit as you like.',
                            benefits: [
                                'Quickly search for fields',
                                'Powerful filter logic like in Salesforce',
                                'Display Cross object in one view',
                            ],
                            benefitsColor: 'yellow',
                            image: tableBulkEditingImage,
                            imageAlt: 'Bulk editing in table',
                        },
                        {
                            title: 'Unbelievably fast',
                            description:
                                'Forget the load and reload of your views to reflect the latest changes. Every update in Laserfocus is instant and synced directly to Salesforce. And Vice-Versa.',
                            benefits: [
                                'Interactions below 100ms - it’s that fast',
                                'Filter for keywords in a split-second with quick search',
                                'Toggle field visibility on/off while keeping filters active',
                            ],
                            benefitsColor: 'red',
                            image: tableLeadsSearchImage,
                            imageAlt: 'Search leads in table',
                        },
                    ]}
                />

                <BannerWithCtaSection
                    title="Try Laserfocus without hassle"
                    description="Start using Laserfocus within 30 seconds. Securely log in via Salesforce. No admin needed."
                />

                <FeatureGroup
                    title="All object infos in one page"
                    image={pagePersonDetailsImage}
                    imageAlt="Person details page with activity feed"
                    imageBgColor="purple"
                    sections={[
                        {
                            title: 'See the past, plan the future',
                            description:
                                'See all interactions with your customers in a clear timeline. Don’t miss out on email conversations, call history, or meeting notes and get the best overview.',
                            benefits: [
                                'Emails, calendar, tasks, notes are easily accessible',
                                'Pin important messages and notes to cut out the noise',
                                'Create tasks, notes and reminders instantly',
                            ],
                            benefitsColor: 'yellow',
                            image: activityStreamBigImage,
                            imageAlt: 'Activity stream',
                        },
                        {
                            title: 'Cluster fields, even from different objects',
                            description:
                                '90% of your fields are irrelevant to your daily work. Combine the relevant ones for you into field groups. It even works with fields from different objects!',
                            benefits: [
                                'Create own field groups and share them',
                                'Cluster the fields according to your process and sales stages',
                                'Personalize your own workflow with your own field groups',
                            ],
                            benefitsColor: 'green',
                            image: fieldGroupsEditingImage,
                            imageAlt: 'editing in field group',
                        },
                    ]}
                />

                <FaqSection
                    title="Frequently asked questions"
                    description="Laserfocus helps you become more productive by offering you all the tools you need to close deals with confidence."
                    elements={[
                        {
                            question: 'How can I try Laserfocus for myself?',
                            answer: 'Connect with one click via Salesforce Oauth and start using Laserfocus. Laserfocus sits on top of your Salesforce data, so there is no setup or installation required.',
                        },
                        {
                            question: 'What does Laserfocus cost?',
                            answer: 'It’s free for individuals and small teams trying out Laserfocus.',
                        },
                        {
                            question: 'Do I need my Salesforce Admin for setup?',
                            answer: [
                                'Nope. Laserfocus doesn’t require any setup or installation.',
                                'You securely log in and connect via Salesforce Oauth with Laserfocus and can start using it instantly if your Salesforce license has API access.',
                            ],
                        },
                        {
                            question: 'How long does the setup need?',
                            answer: 'Like everything in Laserfocus: it’s superfast. One click and you are ready. Means 30 seconds.',
                        },
                        {
                            question: 'Are you changing my Salesforce setup?',
                            answer: [
                                'No. Salesforce is defined and setup by your company, we don’t interact with your workflows in Salesforce or create any fields.',
                                'Everything stays untouched in your Salesforce instance.',
                            ],
                        },
                        {
                            question: 'How much time can Laserfocus save me?',
                            answer: 'Glad you asked. Just think about how much time you spend in Salesforce per day and cut that by 80%.',
                        },
                    ]}
                />

                <TeaserCardSliderSection />

                <ClosingSection
                    title="Streamline your sales process now"
                    description="Don’t let complex processes and tools slow you down."
                />
            </div>
        </PageLayout>
    )
}

export default FeaturesPage
